import { type StateCreator } from 'zustand';
import { captureHostInputs } from '../configure/host-inputs-facade';
import { DeviceInUseError, MicPermissionError } from '../types';
import { getMicPermission } from '../useSessionRecordingStore/lib/getMicPermission';
import { watchMicPermissionGrant } from '../useSessionRecordingStore/lib/queryMicPermissionState';
import { type AudioGraphStore } from './types';

export interface MicSlice {
  isMicBlocked: boolean;
  testMic: () => Promise<void>;
  blockMic: () => void;
  unblockMic: () => void;
}

const isNotFoundCause = (err: unknown): boolean =>
  err instanceof MicPermissionError &&
  err.cause instanceof DOMException &&
  err.cause.name === 'NotFoundError';

export const createMicSlice: StateCreator<AudioGraphStore, [], [], MicSlice> = (
  set,
  get
) => {
  let stopPermissionWatch: (() => void) | null = null;

  return {
    isMicBlocked: false,

    testMic: async () => {
      const { addError, clearError } = get();

      // A denied stream request repeats on every rebuild and device poll, and a
      // query-granted answer can't lift it ("Permission denied by system" reports
      // granted), so stay blocked until the user acts or the permission changes.
      if (get().isMicBlocked) {
        addError('MIC_DISALLOWED');
        return;
      }

      let micCheck: Awaited<ReturnType<typeof getMicPermission>>;
      try {
        micCheck = await getMicPermission();
      } catch (err) {
        // An acquisition failure is not a permission denial — recording it as
        // MIC_DISALLOWED would surface "grant mic access" copy even though the
        // browser may have failed because of contention or a device issue.
        if (err instanceof DeviceInUseError) {
          addError('DEVICE_IN_USE');
          // A busy-device rejection also proves permission was granted; a
          // stale MIC_DISALLOWED would otherwise outrank the fresh code in
          // mini-mode's precedence order.
          clearError('MIC_DISALLOWED');
        } else if (isNotFoundCause(err)) {
          addError('NO_DEVICES');
        } else if (captureHostInputs.isNativeCaptureHost()) {
          addError('MIC_DISALLOWED');
        } else {
          get().blockMic();
        }
        throw err;
      }

      clearError('MIC_DISALLOWED');
      // Only a real stream acquisition proves the device is free — the
      // query-granted fast path never touches the device, so clearing there
      // would hide a genuinely busy mic.
      if (micCheck === 'stream-verified') {
        clearError('DEVICE_IN_USE');
      }
    },

    blockMic: () => {
      set({ isMicBlocked: true });
      get().addError('MIC_DISALLOWED');
      stopPermissionWatch?.();
      stopPermissionWatch = watchMicPermissionGrant(() => {
        get().clearError('MIC_DISALLOWED');
        // A full start, not a rebuild: the denied start never marked the graph
        // ready. The task queue logs a failure, and a repeat denial blocks again.
        get()
          .startTask()
          .catch(() => undefined);
      });
    },

    unblockMic: () => {
      stopPermissionWatch?.();
      stopPermissionWatch = null;
      if (get().isMicBlocked) set({ isMicBlocked: false });
    },
  };
};
